import React, { useEffect } from 'react';
import { Form, Button, Space, Spin, Card } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useUnsaved } from '@/contexts/UnsavedContext';
import useBlockNavigation from '@/components/AppForm/useBlockNavigation';
import { LoadingOutlined } from '@ant-design/icons';

const AppForm = ({
  initialValues = {},
  onSubmit,
  onCancel,
  targetPath,
  children,
  title,
  formButtons,
  hasButtons = true,
  noCancel = false,
  buttonProps = {},
  isCard = true,
  form,
  ...props
}) => {
  const navigate = useNavigate();
  const { hasUnsaved, setHasUnsaved } = useUnsaved();
  const { blockNavigation } = useBlockNavigation(
    hasUnsaved,
    'You have unsaved changes. Are you sure you want to leave?',
    async () => {
      await navigate(targetPath ? targetPath : -1, { replace: true });
      setHasUnsaved(false);
    }
  );

  useEffect(() => {
    return () => {
      form?.resetFields();
      setHasUnsaved(false);
    };
  }, [setHasUnsaved, form]);

  const handleFinish = async (values) => {
    if (onSubmit) {
      await onSubmit(values);
      setHasUnsaved(false);
    }
  };

  const handleValuesChange = (changedValues, allValues) => {
    const touched = form?.isFieldsTouched();
    const hasChanges = touched;
    if (hasChanges !== hasUnsaved) {
      setHasUnsaved(hasChanges);
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      if (form?.isFieldsTouched() && hasUnsaved) {
        blockNavigation(targetPath);
      } else {
        navigate(targetPath || -1, { replace: true });
      }
    }
  };

  useEffect(() => {
    if (initialValues) {
      form?.setFieldsValue(initialValues);
    }
  }, [initialValues, form]);

  const formContent = (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleFinish}
      onValuesChange={handleValuesChange}
      {...props}
    >
      {children}

      {hasButtons && (
        <Form.Item
          style={{
            textAlign: 'right',
            marginBottom: 0,
          }}
        >
          {formButtons ? (
            formButtons
          ) : (
            <Space className="mt-6">
              {!noCancel && (
                <Button type="text" className="font-medium size-16 h-8" onClick={handleCancel}>
                  Cancel
                </Button>
              )}
              <Button
                type="primary"
                htmlType="submit"
                className="font-medium size-16 h-8"
                disabled={
                  buttonProps?.saveDisabled !== undefined
                    ? buttonProps?.saveDisabled
                    : !form?.isFieldsTouched() || buttonProps?.disabled
                }
              >
                {buttonProps?.loading ? (
                  <Spin
                    indicator={
                      <LoadingOutlined
                        style={{
                          fontSize: 16,
                        }}
                      />
                    }
                  />
                ) : (
                  'Save'
                )}
              </Button>
            </Space>
          )}
        </Form.Item>
      )}
    </Form>
  );

  return isCard ? <Card className="shadow-sm">{formContent}</Card> : formContent;
};

export default AppForm;
